import { isEqual, isNil } from "lodash-es";
import type { FieldType } from "../types";
import type FieldId from "../fields/FieldId";
import { CLOSING_ANIMATION_DURATION_IN_MILLISECONDS } from "../components/drawer/Drawer";

// A pattern that matches field id's generated by the old platform, for example; d8ea9e5a7e5deac27378563a0f283272
export const OLD_FIELD_ID_PATTERN = /^[0-9a-f]{32}$/;

export const getChanges = (oldObj: Record<string, unknown>, newObj: Record<string, unknown>): Record<string, unknown> =>
  Object.entries(newObj)
    .filter(([key, value]) => !isEqual(oldObj[key], value))
    .reduce(
      (acc, [key, value]) => {
        acc[key] = value;
        return acc;
      },
      {} as Record<string, unknown>,
    );

export const mapKeyToPropertyKey = (obj: Record<string, unknown>) =>
  Object.entries(obj).map(([key, value]) => ({ propertyKey: key, value }));

export const SEARCHABLE_FIELDS: FieldType[] = [
  "address",
  "barcode",
  "calculation",
  "checkbox",
  "currentLocation",
  "date",
  "dateTime",
  "email",
  "iban",
  "licensePlate",
  "location",
  "lookup",
  "number",
  "phone",
  "price",
  "radio",
  "rating",
  "readOnlyText",
  "search",
  "slider",
  "smiley",
  "text",
  "textArea",
  "time",
  "timeDifference",
];

export const SUMMARIZABLE_FIELDS: FieldType[] = [
  "address",
  "barcode",
  "calculation",
  "checkbox",
  "currentLocation",
  "date",
  "dateTime",
  "drawing",
  "email",
  "iban",
  "licensePlate",
  "location",
  "lookup",
  "number",
  "phone",
  "photo",
  "price",
  "radio",
  "rating",
  "readOnlyText",
  "search",
  "signature",
  "slider",
  "smiley",
  "stopwatch",
  "text",
  "textArea",
  "time",
  "timeDifference",
];

export const RECIPIENT_ALLOWED_PLACEHOLDER_FIELDS: FieldType[] = ["email", "lookup", "radio", "search"];

export const scrollToField = (fieldId: FieldId, behavior: "smooth" | "instant") => {
  const id = fieldId.toString();
  const deadline = Date.now() + CLOSING_ANIMATION_DURATION_IN_MILLISECONDS;

  const scroll = () => {
    const element = document.getElementById(id);

    if (isNil(element)) {
      if (Date.now() < deadline) {
        requestAnimationFrame(scroll);
      }
      return;
    }

    const scrollContainer = element.closest("main");
    if (isNil(scrollContainer)) {
      element.scrollIntoView({ behavior, block: "center" });
      return;
    }

    // Compute the element's position relative to the scroll container
    const containerRect = scrollContainer.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const relativeTop = elementRect.top - containerRect.top + scrollContainer.scrollTop;
    const targetScrollTop = relativeTop - containerRect.height / 2 + elementRect.height / 2;
    scrollContainer.scrollTo({ top: targetScrollTop, behavior });
  };

  requestAnimationFrame(scroll);
};
